import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectValue } from 'antd/lib/select';
import { useHistory } from 'react-router';

import { EmbeddingSettings, ProviderModel, ProviderModelType } from '../../../gamma-api';
import IbSelect from '../common/IbSelect';
import IbButton from '../common/IbButton';
import IbTypography from '../common/IbTypography';

import { buildModelSelectorOptions, buildModelSelectorValue, parseModelSelectorValue } from './utils';
import { MAIN_CLASS_NAME } from './const';

export interface IEmbeddingModelSelectorProps {
  loading?: boolean;
  settings?: EmbeddingSettings;
  providers: ProviderModel[];
  status?: 'default' | 'success' | 'error';
  descriptor?: ReactNode;
  onChange: (settings: EmbeddingSettings) => void;
  onBlur?: () => void;
}

const EmbeddingModelSelector: React.FC<IEmbeddingModelSelectorProps> = ({
  loading,
  settings,
  providers,
  status,
  descriptor,
  onChange,
  onBlur,
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();

  const options = buildModelSelectorOptions(providers, ProviderModelType.Embedding);
  const selectorDisabled = !loading && !options.length;
  const modelValue =
    selectorDisabled || loading ? undefined : buildModelSelectorValue(settings?.provider_id, settings?.model_name);

  const selectedModelValue = modelValue && options.some((o) => o.value === modelValue) ? modelValue : undefined;

  const onModelChange = (value: SelectValue) => {
    const [providerId, modelName] = parseModelSelectorValue(value);
    onChange({
      ...settings,
      provider_id: providerId,
      model_name: modelName,
    });
  };

  const onGoToSectionButtonClick = () => push('/providers');

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbSelect
        disabled={selectorDisabled}
        loading={loading}
        options={options}
        status={status}
        value={selectedModelValue}
        onBlur={onBlur}
        onChange={onModelChange}
      />
      {selectorDisabled && (
        <IbTypography.Paragraph disabled type="descriptor">
          {t('Model selector: No connected model providers.')}
          <IbButton type="link" onClick={onGoToSectionButtonClick}>
            {t('Model selector: Go to section')}
          </IbButton>
        </IbTypography.Paragraph>
      )}
      {descriptor}
    </div>
  );
};

export default EmbeddingModelSelector;
